import { defineNuxtPlugin, useRuntimeConfig } from "nuxt/app";
import type { FeatureFlagService, IContext } from "./types";
import { UnleashBrowser } from "./services/unleash-browser";
import { UnleashServer } from "./services/unleash-server";
import { FalsyMock } from "./services/falsy-mock";
import { useFeatureFlagStore } from "./pinia";
import type { Flag } from "./flag";
import { usePinia } from "#imports";

export default defineNuxtPlugin(({ vueApp }) => {
  const config = useRuntimeConfig();
  const pinia = usePinia();
  const featureFlagStore = useFeatureFlagStore(pinia);
  let client: FeatureFlagService;

  if (!config.public.featureFlag.enabled) {
    client = new FalsyMock();
  } else if (process.client) {
    client = new UnleashBrowser(
      config.public.featureFlag.url,
      config.public.featureFlag.clientKey,
      config.public.featureFlag.appName,
    );
  } else {
    client = new UnleashServer(
      config.public.featureFlag.url,
      config.public.featureFlag.clientKey,
      config.public.featureFlag.appName,
    );
  }

  try {
    client.connect();
  } catch (e) {
    vueApp.$nuxt.$bugReporter.report(e);
  }

  return {
    provide: {
      featureFlag: {
        isEnabled: async (flag: Flag) => {
          try {
            const value = await client.isEnabled(flag);
            featureFlagStore.setFlag(flag as Flag, value);
            return value;
          } catch (e: unknown) {
            vueApp.$nuxt.$bugReporter.report(e);
            featureFlagStore.setFlag(flag, false);
            return false;
          }
        },
        loadBasicFlags: () => {
          try {
            // Load basic flags that are essential for the application
            // Used when necessary only
          } catch (e: unknown) {
            vueApp.$nuxt.$bugReporter.report(e);
          }
        },
        setContextField: (field: keyof IContext, value?: string) => {
          client.setContextField(field, value);
        },
        getContext: () => {
          client.getContext();
        },
        identify: (userId?: string) => {
          client.identify(userId);
        },
        getUserId: () => {
          client.getUserId();
        },
      },
    },
  };
});
